import { h, Host, Listen, Element, Component, Prop, Watch, State, Event, EventEmitter } from '@stencil/core';
import state from './state';
import { getViewportValue, addMobileClass } from './utils';
import { createFocusTrap, FocusTrap } from '../../utils/focus-trap';

import type { Content, Screen, Mode, Layout, SubmitEventArg } from './types';
import type { HighlightableElement } from './components/clickable-element';
import type { DropdownOption, WithHTML } from './components/dropdown/dropdown-input';

function isDropdownOptionWithHtml(option: DropdownOption): option is WithHTML {
  return 'html' in option && typeof option.html === 'function';
}

@Component({
  tag: 'acquisition-overlay',
  styleUrls: ['./acquisition-overlay.css', './components/dropdown/dropdown-options.css'],
  shadow: true,
})
export class AcquisitionOverlay {
  anchorElements: HTMLAnchorElement[] = [];
  private focusTrap: FocusTrap | null = null;

  @Element() el: HTMLElement;

  //values - The values for attributes that will be used in the overlay, eg: close button colour.
  // the JSON string will needs to be encoded (encodeURIComponent) before being passed to the component
  @Prop() values: string | (Content & { layout?: Layout }) = '';

  //forcemobileview - this is for console2 overlay builder mobile view.
  @Prop() forcemobileview: boolean = false;

  //mode - The mode of the overlay.
  @Prop() mode: Mode = 'live';

  //changescreen - The screen that will be displayed in the overlay.
  @Prop() screen: Screen = 'email';

  // pass config.key from plugin
  @Prop() account: string;

  // used to highlight a specific element in the overlay in console2
  @Prop() highlightedClickableElement: HighlightableElement | null = null;

  // used to prevent the overlay from highlighting elements in console2
  @Prop() preventHighlight = false;

  // overlayId - The id of the overlay.
  @Prop() overlayId: string;

  // layout - The layout of the overlay.
  @Prop() layout: Layout = { name: 'default' };

  @State() numImagesToLoad = 0;

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.checkForMobileViewport(window.innerWidth);
  }

  @Listen('keyup', { target: 'window' })
  handleEscapeKey(e: KeyboardEvent) {
    this.onEscapeKey(e);
  }

  @Listen('om:overlay_close')
  handleOverlayClose() {
    this.deactivateFocusTrap();
  }

  @Event({
    eventName: 'om:overlay_submit',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  submitEvent: EventEmitter<SubmitEventArg>;

  submitEventHandler(data: SubmitEventArg) {
    if (state.mode !== 'live') {
      return;
    }
    this.submitEvent?.emit(data);
  }

  @Event({
    eventName: 'om:overlay_close',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  closeEvent: EventEmitter;

  closeEventHandler() {
    if (this.mode === 'preview') {
      return;
    }

    this.closeEvent?.emit();
  }

  @Watch('values')
  parseValues(newValues: string | (Content & { layout?: Layout })) {
    let data: Content & { layout?: Layout };

    if (typeof newValues === 'string') {
      const decodedValues = newValues ? decodeURIComponent(newValues) : null;
      if (decodedValues) {
        data = JSON.parse(decodedValues);
      }
    } else {
      data = newValues;
    }
    const { layout, email, sms, success } = data;
    state.layout = layout ?? {};
    state.content = { email, sms, success };
  }

  @Watch('screen')
  setCurrentScreen(newValue: Screen) {
    if (this.mode === 'preview' && ['email', 'sms', 'success'].includes(newValue)) {
      state.currentScreen = newValue;
    }

    state.highlightElement = { field: null };
  }

  @Watch('highlightedClickableElement')
  handlePropChangeHighlightedClickableElement(newValue: HighlightableElement) {
    state.highlightElement = newValue;
  }

  checkForMobileViewport(windowWidth: number) {
    if (this.forcemobileview) return;
    state.isMobileViewport = windowWidth <= 568;
  }

  handleBackgroundClick(event: Event) {
    if (state.mode === 'preview') return;

    const target = event.target as HTMLElement;
    if (target.classList.contains('full-screen') || target.classList.contains('overlayInner-container')) {
      if (state.openDropdown !== null) {
        state.openDropdown = null;
        state.currentScreenContainer?.classList.remove('overflow-hidden');
      } else {
        this.unmount();
      }
    }
  }

  unmount() {
    this.deactivateFocusTrap();
    //TODO: dispatch custom event to trigger clean up method from the plugin
    document.getElementsByTagName('acquisition-overlay')[0]?.remove();
    this.closeEventHandler();
  }

  private initializeFocusTrap() {
    if (!this.el.shadowRoot) return;

    this.focusTrap = createFocusTrap({
      container: this.el.shadowRoot,
      onEscape: () => {
        if (this.mode !== 'preview') {
          this.unmount();
        }
      },
      trapFocus: this.mode !== 'preview',
    });
  }

  private activateFocusTrap() {
    if (this.focusTrap) {
      this.focusTrap.activate();
    }
  }

  private deactivateFocusTrap() {
    if (this.focusTrap) {
      this.focusTrap.deactivate();
      this.focusTrap = null;
    }
  }

  renderDropdownOptions() {
    if (!state.openDropdown) return null;
    return (
      <ul
        role="listbox"
        id={`${state.openDropdown.id}-options`}
        style={state.openDropdown.style}
        class={addMobileClass('dropdown-selector')}
      >
        {state.openDropdown.options.map(option => {
          return (
            <li
              role="option"
              tabindex="0"
              class="dropdown-option-container"
              onClick={() => state.openDropdown.onOptionSelect(option)}
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  state.openDropdown.onOptionSelect(option);
                }
              }}
            >
              {isDropdownOptionWithHtml(option) ? option.html() : option.label}
            </li>
          );
        })}
      </ul>
    );
  }

  private template() {
    const screenValues = state.content[state.currentScreen];

    return (
      <Host
        style={{
          ...('cta' in screenValues && {
            '--submit-button-bg-color': getViewportValue(screenValues.cta, 'backgroundColour'),
            '--submit-button-border-radius': `${getViewportValue(screenValues.cta, 'borderRadius') || 0}px`,
          }),
          ...('input' in screenValues && {
            '--input-border-radius': `${getViewportValue(screenValues.input, 'borderRadius') || 0}px`,
          }),
          ...(state.layout?.borderRadius && {
            '--layout-border-radius': `${state.layout.borderRadius}px`,
          }),
        }}
      >
        <div
          id="om_overlay_backdrop"
          class={`
          overlay-container

          ${this.preventHighlight ? 'no-pointer' : ''}
          ${this.mode === 'preview' ? 'preview-screen' : 'full-screen'}
          ${this.forcemobileview || state.isMobileViewport ? 'is-mobile-viewport' : ''}
        `}
          onClick={e => {
            this.handleBackgroundClick(e);
          }}
        >
          {(() => {
            const commonProps = {
              screen: this.mode === 'preview' ? this.screen : state.currentScreen,
              unmount: this.unmount.bind(this),
              handleSubmit: this.submitEventHandler.bind(this),
            };
            const { name: layoutName, ...layoutProps } = state.layout;

            switch (layoutName) {
              case 'default':
                return <om-default-layout {...commonProps} {...layoutProps} />;
              case 'split':
                return <om-split-layout {...commonProps} {...layoutProps} />;
              case 'positioned':
                return <om-positioned-layout {...commonProps} {...layoutProps} />;
              default:
                return <om-default-layout {...commonProps} />;
            }
          })()}
          {this.renderDropdownOptions()}
        </div>
      </Host>
    );
  }

  componentWillLoad() {
    try {
      // these attrs shouldn't change but I need them
      // in state so other components can access them
      state.mode = this.mode;
      state.preventHighlight = this.preventHighlight;
      state.account = this.account;
      state.overlayId = this.overlayId;
      state.shadowRoot = this.el.shadowRoot;
      state.isMobileViewport = this.forcemobileview;
      state.currentScreen = this.screen;

      this.parseValues(this.values);
      state.currentScreen = this.mode === 'preview' ? this.screen : state.content.email.shouldHide ? 'sms' : 'email';

      if (!state.content) {
        return;
      }

      // preload gstatic
      const head = document.head;
      const linkGstatic = document.createElement('link');
      linkGstatic.href = 'https://fonts.gstatic.com';
      linkGstatic.rel = 'preconnect';
      linkGstatic.crossOrigin = '';
      head.appendChild(linkGstatic);

      // preload googleapis
      const linkGoogleapis = document.createElement('link');
      linkGoogleapis.href = 'https://fonts.googleapis.com';
      linkGoogleapis.rel = 'preconnect';
      head.appendChild(linkGoogleapis);

      const link = document.createElement('link');
      link.href =
        'https://fonts.googleapis.com/css2?family=GFS+Didot&family=Lato&family=Open+Sans&family=Roboto&family=Libre+Baskerville&family=Montserrat&display=swap';
      link.rel = 'stylesheet';
      head.appendChild(link);
      const addLinkToHead = (url: string, rel: 'prefetch' | 'preload') => {
        const link = document.createElement('link');
        link.rel = rel;
        link.as = 'image';
        link.href = url;

        if (rel === 'preload') {
          link.fetchPriority = 'high';

          this.numImagesToLoad = this.numImagesToLoad + 1;
          link.addEventListener('load', () => {
            this.numImagesToLoad = this.numImagesToLoad - 1;
          });
        }

        head.appendChild(link);
      };

      // preload images and append to head
      Object.entries(state.content).forEach(([screenName, screen]: [keyof Content, Content[keyof Content]]) => {
        const rel = screenName === state.currentScreen ? 'preload' : 'prefetch';
        const { background } = screen ?? {};
        if (!background) return;

        const { computer, mobile } = background;

        if (computer?.imageUrl) {
          addLinkToHead(computer?.imageUrl, rel);
        }

        if (mobile?.imageUrl) {
          addLinkToHead(mobile?.imageUrl, rel);
        }
      });
    } catch (error) {
      console.error('Error parsing values', error);
      return null;
    }
  }

  onEscapeKey(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;

    if (state.openDropdown) {
      state.openDropdown = null;
    } else if (this.mode !== 'preview') {
      this.unmount();
    }
  }

  componentDidRender() {
    this.preventAnchorPropagation();
    this.checkForMobileViewport(window.innerWidth);
    this.handlePropChangeHighlightedClickableElement(this.highlightedClickableElement);

    if (!this.focusTrap) {
      this.initializeFocusTrap();
    }
    this.activateFocusTrap();
  }

  disconnectedCallback() {
    this.deactivateFocusTrap();
  }

  preventAnchorPropagation() {
    if (this.mode !== 'preview') return;
    const eventListener = [
      'click',
      (e: MouseEvent) => {
        e.stopPropagation();
      },
    ] as const;

    while (this.anchorElements.length) {
      this.anchorElements.pop().removeEventListener(...eventListener);
    }

    this.el.shadowRoot.querySelectorAll('a').forEach(el => {
      el.addEventListener(...eventListener);
      this.anchorElements.push(el);
    });
  }

  render() {
    if (this.numImagesToLoad > 0) return;
    return this.template();
  }
}
